import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Camera, Copy, ExternalLink } from 'lucide-react'
import { PageHeader, Tabs, Button, Chip, Banner, EmptyState, FormRow, Textarea, Modal, Select, useToast } from '../ds/index'
import { PhoneAlerts } from '../components/PhoneAlerts'
import { OwnerMessages } from '../components/OwnerMessages'
import { useApi } from '../lib/useApi'
import { post, put } from '../lib/api'
import { useSession } from '../lib/session'
import { ago, title } from '../lib/format'
import type { MyLead, Question } from '../lib/types'
import { PostCard } from '../components/PostCard'
import { Icon } from '../components/Icon'
import { LoadingBlock, ErrorBlock } from '../components/Loading'
import { RateBar } from '../components/Viz'
import { duration } from './Dashboard'
import { ScoreChip } from '../components/ScoreChip'
import ScreenshotReader from './ScreenshotReader'
import { SortSelect, useSortParam, useRememberedList, shortName, type LeadSort } from '../components/SortSelect'

type Status = 'open' | 'responded' | 'skipped'

// Newest first by default. Best first (by score) is a choice on the To reply queue only; the history tabs have no score to go by.
const OPEN_SORTS: LeadSort[] = ['best', 'newest', 'oldest', 'az', 'za']
const HISTORY_SORTS: LeadSort[] = ['newest', 'oldest', 'az', 'za']

export const URGENCY: Record<string, { label: string; tone?: 'neg' | 'warn' }> = {
  today: { label: 'Needs it today', tone: 'neg' },
  this_week: { label: 'This week', tone: 'warn' },
  flexible: { label: 'Planning ahead' },
  unknown: { label: 'No timing given' },
}

export default function MyLeads() {
  const [status, setStatus] = useState<Status>('open')
  const sorts = status === 'open' ? OPEN_SORTS : HISTORY_SORTS
  const [sort, setSort] = useSortParam(sorts, 'newest')
  const { data, error, loading, reload } = useApi<{ leads: MyLead[]; counts: Record<string, number>; week: { assigned: number; responded: number; median_response_s: number | null } }>(`/api/my/leads?status=${status}&sort=${sort}`)
  const { me, refresh } = useSession()
  // A rep who opens a lead, goes to Facebook and comes back lands on the same order and filters (Janelle, 2026-11-02).
  useRememberedList(me ? `hp-my-leads:${me.user.id}` : null, ['sort', 'group', 'cat'])

  // New leads arrive without a page load. Thirty seconds is fast enough for a
  // reply that is worth minutes, and cheap.
  useEffect(() => {
    const t = setInterval(() => { reload(); void refresh() }, 30_000)
    return () => clearInterval(t)
  }, [reload, refresh])

  const counts = data?.counts || {}
  // Category filter: the categories in this tab, busiest first, kept in the address and remembered (above).
  const [params, setParams] = useSearchParams()
  const cat = params.get('cat') || ''
  const grp = params.get('group') || ''
  const catOf = (l: MyLead) => l.category_name || 'Other'
  const groupOf = (l: MyLead) => l.group_name || 'Unknown group'
  const all = data?.leads || []
  // Each filter's counts follow the other filter, so the numbers always match what a tap would show.
  const tally = (ls: MyLead[], key: (l: MyLead) => string) => Object.entries(ls.reduce<Record<string, number>>((m, l) => { m[key(l)] = (m[key(l)] || 0) + 1; return m }, {}))
    .sort((x, y) => y[1] - x[1] || x[0].localeCompare(y[0]))
  const inGroup = all.filter((l) => !grp || groupOf(l) === grp)
  const cats = tally(inGroup, catOf)
  const groups = tally(all.filter((l) => !cat || catOf(l) === cat), groupOf)
  const shown = inGroup.filter((l) => !cat || catOf(l) === cat)
  const setParam = (k: string, v: string) => setParams((p) => { const n = new URLSearchParams(p); if (v) n.set(k, v); else n.delete(k); return n }, { replace: true })
  const pick = (c: string) => setParam('cat', c)
  return (
    <>
      <PageHeader title="My leads" subtitle="People in your groups asking for something one of your businesses does. Newest first unless you pick another order. Reply while it is fresh."
        actions={<NewFromScreenshots onDone={reload} />} />
      <PhoneAlerts compact />
      {data?.week && data.week.assigned > 0 && (
        <div className="rep-week">
          <span><strong className="num">{data.week.responded}</strong> of <strong className="num">{data.week.assigned}</strong> replied this week</span>
          <RateBar num={data.week.responded} den={data.week.assigned} />
          {data.week.median_response_s != null && <span className="muted">typical reply {duration(data.week.median_response_s)}</span>}
        </div>
      )}
      <Tabs<Status> value={status} onChange={setStatus} tabs={[
        { value: 'open', label: 'To reply', count: counts.open || 0 },
        { value: 'responded', label: 'Replied', count: counts.responded || 0 },
        { value: 'skipped', label: 'Skipped', count: counts.skipped || 0 },
      ]} />
      <div className="group-filter">
        {(groups.length > 1 || grp) && (
          <Select aria-label="Filter by group" value={grp} onChange={(e) => setParam('group', e.target.value)} className={grp ? 'is-set' : undefined}>
            <option value="">All groups ({all.filter((l) => !cat || catOf(l) === cat).length})</option>
            {groups.map(([g, n]) => <option key={g} value={g}>{g} ({n})</option>)}
            {grp && !groups.some(([g]) => g === grp) && <option value={grp}>{grp} (0)</option>}
          </Select>
        )}
        <SortSelect value={sort} onChange={setSort} choices={sorts} />
      </div>
      {(cats.length > 1 || cat) && (
        <div className="cat-filter" role="group" aria-label="Filter by category">
          <button type="button" className="filter-chip" aria-pressed={!cat} onClick={() => pick('')}>All <span className="cat-count">{inGroup.length}</span></button>
          {cats.map(([c, n]) => (
            <button key={c} type="button" className="filter-chip" aria-pressed={cat === c} onClick={() => pick(cat === c ? '' : c)}>{c} <span className="cat-count">{n}</span></button>
          ))}
          {cat && !cats.some(([c]) => c === cat) && (
            <button type="button" className="filter-chip" aria-pressed onClick={() => pick('')}>{cat} <span className="cat-count">0</span></button>
          )}
        </div>
      )}
      <div style={{ marginTop: 'var(--s-6)' }}>
        {error && <ErrorBlock error={error} />}
        {loading && !data ? <LoadingBlock /> : data && data.leads.length === 0 ? (
          <EmptyState title={status === 'open' ? 'Nothing waiting' : 'Nothing here yet'} body={status === 'open' ? 'New requests from your groups show up here within a minute of the post.' : undefined} />
        ) : data && shown.length === 0 ? (
          // A remembered filter can outlive its leads: say so, and give the way out.
          <EmptyState title="Nothing in this filter" body="Your other leads are under All."
            action={<Button onClick={() => setParams((p) => { const n = new URLSearchParams(p); n.delete('cat'); n.delete('group'); return n }, { replace: true })}>Show all</Button>} />
        ) : (
          <ul className="rep-list">
            {shown.map((l) => (
              <li key={l.id}>
                <Link to={`/my/${l.id}`} className="rep-item">
                  <div className="rep-item-top">
                    <span className="nowrap">{[shortName(l.person_name || l.author_display), l.group_name].filter(Boolean).join(' · ')}</span>
                    <span className="nowrap">{ago(l.first_seen_at)}</span>
                  </div>
                  <div className="rep-item-summary">{l.summary || l.body_text?.slice(0, 120)}</div>
                  <div className="row-tight" style={{ flexWrap: 'wrap' }}>
                    <ScoreChip score={l.lead_score} />
                    {l.urgency && URGENCY[l.urgency]?.tone && <Chip tone={URGENCY[l.urgency].tone} dot>{URGENCY[l.urgency].label}</Chip>}
                    {l.match === 'held_back' && <Chip tone="warn" dot>{heldBackLabel(l)}</Chip>}
                    {l.category_name && <Chip>{l.category_name}</Chip>}
                    {l.area_name ? <Chip>{l.area_name}</Chip> : <Chip tone="warn">Area unclear</Chip>}
                    {!l.opened_at && status === 'open' && <Chip tone="accent" dot>New</Chip>}
                  </div>
                  {l.buyers?.length || l.targets?.length ? <div className="rep-item-for">Recommend <strong>{[...l.buyers, ...(l.targets || [])].map((b) => b.name).join(', ')}</strong></div> : null}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  )
}

export function MyLeadDetail() {
  const { id } = useParams()
  const { data, error, reload } = useApi<{ lead: MyLead; questions: Question[] }>(`/api/my/leads/${id}`)
  const [skipping, setSkipping] = useState(false)
  const nav = useNavigate()
  const { refresh } = useSession()
  const lead = data?.lead

  if (error) return <ErrorBlock error={error} />
  if (!lead) return <LoadingBlock />

  const done = () => { void refresh(); nav('/my') }

  return (
    <div className="stack" style={{ maxWidth: 720 }}>
      <div><Link to="/my" className="btn btn-ghost"><Icon of={ArrowLeft} size={15} /> My leads</Link></div>
      <div>
        <h1 className="page-header-title" style={{ fontSize: 22 }}>{lead.summary || (lead.match === 'held_back' ? 'A post in your group' : 'A request in your group')}</h1>
        <div className="row-tight" style={{ flexWrap: 'wrap', marginTop: 'var(--s-3)' }}>
          {lead.urgency && <Chip tone={URGENCY[lead.urgency]?.tone} dot={Boolean(URGENCY[lead.urgency]?.tone)}>{URGENCY[lead.urgency]?.label}</Chip>}
          {lead.category_name && <Chip>{lead.category_name}</Chip>}
          {lead.area_name ? <Chip>{lead.area_name}{lead.area_text && lead.area_text.toLowerCase() !== lead.area_name.toLowerCase() ? ` (“${lead.area_text}”)` : ''}</Chip> : <Chip tone="warn">Area unclear</Chip>}
        </div>
      </div>

      {lead.status !== 'skipped' && <SuggestedReply id={lead.id} postUrl={lead.post_url} lead={lead} />}

      {lead.buyers?.length || lead.targets?.length ? (
        <div className="recommend">
          <span className="s-label muted">{lead.buyers.length + (lead.targets?.length || 0) > 1 ? `Recommend all ${lead.buyers.length + (lead.targets?.length || 0)}, in one reply` : 'Recommend'}</span>
          {lead.buyers.map((b) => (
            <div key={b.id} className="recommend-one">
              <span className="recommend-name">{b.name} <Link to={`/my/clients?c=${b.id}`} className="recommend-profile">Profile</Link></span>
              {b.pitch && <span>{b.pitch}</span>}
              <span className="muted" style={{ fontSize: 'var(--t-ui)' }}>{[b.phone, b.website].filter(Boolean).join(' · ')}</span>
              <OwnerMessages id={b.id} />
            </div>
          ))}
          {(lead.targets || []).map((t) => (
            <div key={t.id} className="recommend-one">
              <span className="recommend-name">{t.name} <span className="recommend-target">Target business</span></span>
              {t.pitch && <span>{t.pitch}</span>}
              <span className="muted" style={{ fontSize: 'var(--t-ui)' }}>{[t.phone, t.website].filter(Boolean).join(' · ')}</span>
              <span className="muted" style={{ fontSize: 'var(--t-ui)' }}>Not a client yet. {title(lead.category_name || 'This trade')} has no client here, so we are sending them business to win them over. Recommend them like any other.</span>
            </div>
          ))}
          {lead.match === 'category_only' && <span className="post-cut">The post does not say where they are. Check before recommending.</span>}
        </div>
      ) : lead.match === 'held_back' ? (
        <Banner tone="warn" title="The AI held this one back">
          {heldBackLabel(lead)}.{lead.screen_reason ? ` ${sentence(lead.screen_reason)}` : ''} If it is a real request, reply like any lead. If not, tap Not a lead so we can tune the filter.
          {lead.status === 'open' && <div style={{ marginTop: 'var(--s-4)' }}><NotALead id={lead.id} onDone={done} /></div>}
        </Banner>
      ) : (
        <Banner tone="warn" title="No matching business">You were sent this because your org notifies every rep in the group. None of your businesses matches it.</Banner>
      )}

      <PostCard groupName={lead.group_name} author={lead.author_display} text={lead.body_text} truncated={lead.text_truncated} at={lead.first_seen_at} url={lead.post_url} byHand={lead.source === 'responder'} />

      <PersonPanel lead={lead} onChanged={reload} />

      {data.questions.length > 0 && lead.answers && (
        <div>
          <div className="row-tight" style={{ marginBottom: 'var(--s-3)' }}><span className="s-label muted">Read of the post</span><ScoreChip score={lead.lead_score} /></div>
          <dl className="answers">
            {data.questions.map((q) => <div key={q.key} style={{ display: 'contents' }}><dt>{q.text}</dt><dd>{title(lead.answers?.[q.key] || 'unclear')}</dd></div>)}
          </dl>
        </div>
      )}

      {lead.status !== 'skipped' && <Details id={lead.id} saved={lead.details} savedAt={lead.details_at} onSaved={reload} />}

      {lead.status === 'open' ? (
        <>
          <Respond id={lead.id} onDone={done} />
          <div><Button variant="ghost" onClick={() => setSkipping(true)}>Skip this one</Button></div>
        </>
      ) : lead.status === 'responded' ? (
        <>
          <Banner tone="pos" title={`Replied ${ago(lead.responded_at)}`}>
            {lead.reply_note}
            {lead.has_image && <div style={{ marginTop: 'var(--s-4)' }}><img className="shot-preview" src={`/api/assignments/${lead.id}/image`} alt="Screenshot of your reply" /></div>}
          </Banner>
          {lead.pricing_mode === 'tiers' && <Conversation lead={lead} onChanged={reload} />}
        </>
      ) : (
        <Banner tone="neutral" title="Skipped">{lead.skip_reason}</Banner>
      )}
      {skipping && <Skip id={lead.id} onClose={() => setSkipping(false)} onDone={() => { setSkipping(false); reload(); done() }} />}
    </div>
  )
}

/**
 * Keep the conversation going and add it here: each screenshot and note is
 * what the lead is graded on, so more real conversation is a better lead for
 * the business. Closed when the rep says they are done, or when the lead is
 * graded at the end of the window.
 */
function Conversation({ lead, onChanged }: { lead: MyLead; onChanged: () => void }) {
  const toast = useToast()
  const [image, setImage] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const input = useRef<HTMLInputElement>(null)
  const closed = Boolean(lead.conversation_closed_at || lead.graded_at)

  const pick = (file: File | undefined) => {
    setErr(null)
    if (!file) return
    if (!/^image\/(png|jpeg|webp)$/.test(file.type)) { setErr('Use a PNG, JPEG or WebP screenshot'); return }
    if (file.size > 5 * 1024 * 1024) { setErr('That screenshot is over 5 MB'); return }
    const r = new FileReader()
    r.onload = () => setImage(String(r.result))
    r.readAsDataURL(file)
  }
  const add = async () => {
    setBusy(true); setErr(null)
    try {
      await post(`/api/my/leads/${lead.id}/conversation`, { image: image || undefined, note: note.trim() || undefined })
      setImage(null); setNote(''); toast('Added', 'pos'); onChanged()
    } catch (e) { setErr((e as Error).message) } finally { setBusy(false) }
  }
  const finish = async () => {
    try { await post(`/api/my/leads/${lead.id}/done`, {}); toast('Conversation closed', 'pos'); onChanged() } catch (e) { toast((e as Error).message, 'neg') }
  }

  return (
    <div className="post-card">
      <strong>The conversation</strong>
      <span className="muted" style={{ fontSize: 'var(--t-ui)' }}>
        {closed
          ? `Closed${lead.graded_at ? ` and graded ${ago(lead.graded_at)}` : ''}.`
          : `Keep talking with them and add each screenshot or note here. The lead is graded on all of it${lead.grade_due_at ? `, by ${new Date(lead.grade_due_at).toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' })} at the latest` : ''}.`}
      </span>
      {lead.conversation.length > 0 && (
        <div className="stack-tight">
          {lead.conversation.map((c) => (
            <div key={c.id} className="stack-tight" style={{ gap: 'var(--s-2)' }}>
              <span className="muted">{ago(c.created_at)}</span>
              {c.has_image && <img className="shot-preview" src={`/api/conversation/${c.id}/image`} alt="Conversation screenshot" />}
              {c.note && <span>{c.note}</span>}
            </div>
          ))}
        </div>
      )}
      {!closed && (
        <>
          <input ref={input} type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = '' }} />
          {image ? <img className="shot-preview" src={image} alt="New screenshot" /> : null}
          <div className="post-actions">
            <Button variant="secondary" onClick={() => input.current?.click()}><Icon of={Camera} size={15} /> {image ? 'Change screenshot' : 'Add screenshot'}</Button>
          </div>
          <FormRow label="Note, optional"><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="They replied, budget about $5k, want a tour this week" /></FormRow>
          {err && <p className="form-error" role="alert">{err}</p>}
          <div className="post-actions">
            <Button onClick={add} disabled={busy || (!image && !note.trim())}>{busy ? 'Saving' : 'Add to the conversation'}</Button>
            <Button variant="ghost" onClick={finish}>I'm done talking with them</Button>
          </div>
        </>
      )}
    </div>
  )
}

/** What the rep learned from the person who posted. Saved on its own, before or after the reply. */
function Details({ id, saved, savedAt, onSaved }: { id: string; saved: string | null; savedAt: string | null; onSaved: () => void }) {
  const [text, setText] = useState(saved || '')
  const [busy, setBusy] = useState(false)
  const toast = useToast()
  useEffect(() => { setText(saved || '') }, [saved])
  const dirty = text.trim() !== (saved || '').trim()
  const save = async () => {
    setBusy(true)
    try { await put(`/api/my/leads/${id}/details`, { details: text }); toast('Saved', 'pos'); onSaved() } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }
  return (
    <div className="stack-tight">
      <FormRow label="What you learned" help={savedAt ? `Saved ${ago(savedAt)}. Your admin sees this with the lead.` : 'Budget, timing, anything the post left out. Your admin sees this with the lead.'}>
        <Textarea aria-label="What you learned" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Budget about $2k, wants it done before Friday, HOA approval needed" />
      </FormRow>
      <div><Button variant="secondary" onClick={save} disabled={busy || !dirty}>{busy ? 'Saving' : 'Save what you learned'}</Button></div>
    </div>
  )
}

function Respond({ id, onDone }: { id: string; onDone: () => void }) {
  const [image, setImage] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const input = useRef<HTMLInputElement>(null)
  const toast = useToast()

  const pick = (file: File | undefined) => {
    setErr(null)
    if (!file) return
    if (!/^image\/(png|jpeg|webp)$/.test(file.type)) { setErr('Use a PNG, JPEG or WebP screenshot'); return }
    if (file.size > 5 * 1024 * 1024) { setErr('That screenshot is over 5 MB'); return }
    const r = new FileReader()
    r.onload = () => setImage(String(r.result))
    r.readAsDataURL(file)
  }

  const send = async () => {
    setBusy(true)
    setErr(null)
    try {
      await post(`/api/my/leads/${id}/respond`, { image, note })
      toast('Marked as replied', 'pos')
      onDone()
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="post-card">
      <strong>After you reply on Facebook</strong>
      <span className="muted" style={{ fontSize: 'var(--t-ui)' }}>Take a screenshot of your comment and add it here. That is the record the business sees.</span>
      <input ref={input} type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = '' }} />
      {image ? <img className="shot-preview" src={image} alt="Your reply screenshot" /> : null}
      <div className="post-actions">
        <Button variant="secondary" onClick={() => input.current?.click()}><Icon of={Camera} size={15} /> {image ? 'Change screenshot' : 'Add screenshot'}</Button>
      </div>
      <FormRow label="Note, optional"><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything the office should know" /></FormRow>
      {err && <p className="form-error" role="alert">{err}</p>}
      <div className="post-actions"><Button onClick={send} disabled={!image || busy}>{busy ? 'Saving' : 'Mark as replied'}</Button></div>
    </div>
  )
}

/** A model's reason as a sentence: a full stop at the end, never two. */
const sentence = (s: string) => /[.!?]$/.test(s.trim()) ? s.trim() : `${s.trim()}.`

/** What the AI made of a post it held back, in a few words. */
function heldBackLabel(l: MyLead) {
  return l.post_stage === 'uncategorized'
    ? `AI: asking for ${(l.suggested_category || 'a service').toLowerCase()}, not a trade we sell yet`
    : 'AI: not a request'
}

/** One tap on a held-back post: the rep agrees with the AI, and the post leaves their list. */
function NotALead({ id, onDone }: { id: string; onDone: () => void }) {
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const go = async () => {
    setBusy(true)
    try { await post(`/api/my/leads/${id}/skip`, { reason: 'Not actually a request' }); toast('Marked not a lead', 'pos'); onDone() }
    catch (e) { toast((e as Error).message, 'neg'); setBusy(false) }
  }
  return <Button variant="secondary" onClick={go} disabled={busy}>Not a lead</Button>
}

function Skip({ id, onClose, onDone }: { id: string; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const reasons = ['Post was deleted', 'Already answered by someone else', 'Not actually a request', 'Outside our area']
  const save = async () => { try { await post(`/api/my/leads/${id}/skip`, { reason }); onDone() } catch (e) { setErr((e as Error).message) } }
  return (
    <Modal title="Skip this lead" onClose={onClose} footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={!reason.trim()}>Skip</Button></>}>
      <div className="stack-tight">
        <div className="pill-list">{reasons.map((r) => <Button key={r} variant={reason === r ? 'primary' : 'secondary'} onClick={() => setReason(r)}>{r}</Button>)}</div>
        <FormRow label="Reason"><Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} /></FormRow>
        {err && <p className="form-error">{err}</p>}
      </div>
    </Modal>
  )
}

/**
 * The message to post when the lead lines up with a client: written by the
 * model from the post and the client's profile, ready to edit and copy. Edits
 * stay on this screen; the rep posts it on Facebook themselves.
 */
type Stage = 'first' | 'follow_up' | 'connect'
type Saved = { id: string; title: string; body: string; stage: string; category_id: string | null; category_name: string | null }
const STAGES: { value: Stage; label: string }[] = [
  { value: 'first', label: 'First reply' }, { value: 'follow_up', label: 'They answered' }, { value: 'connect', label: 'Connect them' },
]

/**
 * The reply box (Ezra's item 2 and the package's saved reply box): a suggested
 * reply for each stage, written from the business's own setup answers, a
 * picker of saved replies, and room to paste your own. Every suggestion is
 * fresh wording; it is a suggestion, never a script.
 */
function SuggestedReply({ id, postUrl, lead }: { id: string; postUrl: string | null; lead?: MyLead }) {
  const { data } = useApi<{ draft: string | null; disclosures?: string[]; missing?: { id: string; name: string; public: boolean; personal: boolean }[]; notes?: string[] }>(`/api/my/leads/${id}/draft`)
  const saved = useApi<{ replies: Saved[] }>('/api/my/saved-replies')
  const toast = useToast()
  const [stage, setStage] = useState<Stage>('first')
  const [text, setText] = useState<string | null>(null)
  const [said, setSaid] = useState('')
  const [busy, setBusy] = useState(false)
  const box = useRef<HTMLTextAreaElement>(null)
  useEffect(() => { if (data?.draft != null && stage === 'first') setText(data.draft) }, [data?.draft]) // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return <div className="suggested-reply"><span className="s-label muted">Suggested reply</span><span className="muted">Writing a suggested reply…</span></div>
  if (data.draft == null) return null
  // The FTC rule: a reply recommending a client keeps the words saying the responder works with them, as written.
  const disclosures = data.disclosures || []
  const reply = text || ''
  const lost = disclosures.length > 0 && reply.trim() !== '' && (stage === 'first' || (lead?.buyers || []).some((b) => reply.includes(b.name)))
    && !disclosures.some((d) => reply.toLowerCase().includes(d.toLowerCase()))
  const putBack = () => { const d = disclosures[0]; setText(`${reply.trimEnd()} ${d.charAt(0).toUpperCase()}${d.slice(1)}.`) }
  const write = async (s: Stage) => {
    setBusy(true)
    try { setText((await post<{ draft: string }>(`/api/my/leads/${id}/draft`, { stage: s, said: s === 'follow_up' ? said : undefined })).draft) }
    catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }
  const pick = (r: Saved) => {
    const b = lead?.buyers?.[0]
    const first = (lead?.author_display || '').split(' ')[0] || ''
    setText(r.body.replace(/\{first_name\}/g, first).replace(/\{business\}/g, b?.name || 'the business')
      .replace(/\{phone\}/g, b?.phone || '').replace(/\{ask_for\}/g, 'the owner').replace(/\{disclosure\}/g, disclosures[0] || ''))
  }
  // The clipboard API first; where a browser or an in-app webview blocks it,
  // select the text and use the older copy command, which most of them allow.
  const copy = async () => {
    const t = text || ''
    let ok = false
    try { await navigator.clipboard.writeText(t); ok = true } catch { /* fall back below */ }
    if (!ok && box.current) {
      box.current.focus(); box.current.select()
      try { ok = document.execCommand('copy') } catch { ok = false }
    }
    toast(ok ? 'Copied. Paste it as your comment on the post.' : 'Could not copy. The text is selected, so copy it yourself.', ok ? 'pos' : 'neg')
  }
  const fits = (saved.data?.replies || []).filter((r) => (r.stage === 'any' || r.stage === stage) && (!r.category_id || r.category_name === lead?.category_name))
  return (
    <div className="suggested-reply">
      <div className="spread wrap-gap" style={{ alignItems: 'center' }}>
        <span className="s-label muted">Suggested reply</span>
        <div className="filter-chips" role="group" aria-label="Which reply">
          {STAGES.map((s) => <button key={s.value} type="button" className="filter-chip" aria-pressed={stage === s.value}
            onClick={() => { setStage(s.value); if (s.value === 'first') setText(data.draft); else if (s.value === 'connect') void write('connect'); else setText('') }}>{s.label}</button>)}
        </div>
      </div>
      {stage === 'follow_up' && (
        <div className="stack-tight">
          <textarea className="field textarea" rows={2} placeholder="Paste what they said" aria-label="What they said" value={said} onChange={(e) => setSaid(e.target.value)} />
          <div><Button variant="secondary" onClick={() => write('follow_up')} disabled={busy}>{busy ? 'Writing' : 'Suggest a reply'}</Button></div>
        </div>
      )}
      {data.draft === '' && stage === 'first' && !text ? <span className="muted">No reply suggested. The AI did not find a request in this post.</span> : (
        <textarea ref={box} className="field textarea" id={`draft-${id}`} aria-label="Your reply" value={text || ''} onChange={(e) => setText(e.target.value)} rows={4} placeholder="Your reply. Paste your own, or pick a saved one." />
      )}
      {lost && (
        <Banner tone="warn" title="This reply no longer says you work with them">
          Every reply recommending a business has to say so, in words like "{disclosures[0]}". <Button variant="secondary" onClick={putBack}>Put them back</Button>
        </Banner>
      )}
      {stage === 'first' && (data.missing || []).map((m) => (
        <span key={m.id} className="muted" style={{ fontSize: 'var(--t-ui)' }}>
          {m.name} has not given us {m.public && m.personal ? 'any facts' : m.personal ? 'a personal fact' : 'a public fact'} yet, so this reply leaves that out. Never fill it in yourself: ask with Message the owner below.
        </span>
      ))}
      <div className="row-tight" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
        <Button onClick={copy} disabled={!text || lost}><Icon of={Copy} size={15} /> Copy reply</Button>
        {stage !== 'follow_up' && <Button variant="ghost" onClick={() => write(stage)} disabled={busy}>{busy ? 'Writing' : 'Write it again'}</Button>}
        {fits.length > 0 && (
          <Select aria-label="Saved replies" value="" onChange={(e) => { const r = fits.find((x) => x.id === e.target.value); if (r) pick(r) }}>
            <option value="">Saved replies</option>
            {fits.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
          </Select>
        )}
        {postUrl && <a className="btn btn-secondary" href={postUrl} target="_blank" rel="noopener noreferrer"><Icon of={ExternalLink} size={15} /> Open the post</a>}
      </div>
      <span className="muted" style={{ fontSize: 'var(--t-ui)' }}>A suggestion to build on. Sound like yourself, never use the same words twice, and use what they tell you to help them most.</span>
      {(data.notes || []).length > 0 && (
        <details style={{ fontSize: 'var(--t-ui)' }}>
          <summary>How to reply</summary>
          <ul style={{ margin: 'var(--s-3) 0 0', paddingLeft: 'var(--s-6)' }}>{data.notes!.map((n) => <li key={n}>{n}</li>)}</ul>
        </details>
      )}
    </div>
  )
}

/**
 * Who the person is, from the rep's screenshots (Ezra's package, section 1). A
 * lead whose location is not known yet says so: their profile's "Lives in"
 * usually settles it.
 */
function PersonPanel({ lead, onChanged }: { lead: MyLead; onChanged: () => void }) {
  const [open, setOpen] = useState(false)
  const unknown = lead.location_status === 'unknown'
  return (
    <div className="panel" style={{ padding: 'var(--s-5) var(--s-6)' }}>
      <div className="spread wrap-gap" style={{ alignItems: 'center' }}>
        <div>
          <span className="s-label muted">The person</span>
          {lead.read_confirmed_at ? (
            <div>{[lead.person_name, lead.person_lives_in ? `lives in ${lead.person_lives_in}` : null].filter(Boolean).join(', ')}
              {lead.profile_url && <> · <a href={lead.profile_url} target="_blank" rel="noopener noreferrer">Profile <Icon of={ExternalLink} size={13} /></a></>}</div>
          ) : <div className="muted">{unknown ? 'We do not know where they are yet. A screenshot of their profile usually tells us.' : 'Add screenshots of their profile and the post, and we fill this in.'}</div>}
        </div>
        <div className="row-tight" style={{ flexWrap: 'wrap' }}>
          <Button variant={unknown && !lead.read_confirmed_at ? 'primary' : 'secondary'} onClick={() => setOpen(true)}><Icon of={Camera} size={15} /> {lead.read_confirmed_at ? 'Add more screenshots' : 'Fill in from screenshots'}</Button>
          <StopContact phone={lead.person_phone || ''} removed={Boolean(lead.phone_removed)} onDone={onChanged} />
        </div>
      </div>
      {unknown && <LocationAnswer id={lead.id} onDone={onChanged} />}
      {open && (
        <Modal title="Fill in from screenshots" onClose={() => setOpen(false)}>
          <ScreenshotReader assignmentId={lead.id} onDone={() => { setOpen(false); onChanged() }} />
        </Modal>
      )}
    </div>
  )
}

/** A post outside the watched groups, made into a lead from screenshots (Ezra, 2026-10-20, item 6). */
function NewFromScreenshots({ onDone }: { onDone: () => void }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button variant="secondary" onClick={() => setOpen(true)}><Icon of={Camera} size={15} /> New lead from screenshots</Button>
      {open && (
        <Modal title="A lead from screenshots" onClose={() => setOpen(false)}>
          <ScreenshotReader onDone={() => { setOpen(false); onDone() }} />
        </Modal>
      )}
    </>
  )
}

/** They asked not to be contacted: onto Handpicked's own list (terms 9.4), and off every lead not yet sent. */
function StopContact({ phone, removed, onDone }: { phone: string; removed: boolean; onDone: () => void }) {
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const [n, setN] = useState(phone)
  const [why, setWhy] = useState('Asked not to be contacted')
  if (removed) return <Chip tone="warn">Phone on a Do Not Call list</Chip>
  const add = async () => {
    try { await post('/api/dnc/own', { number: n, reason: why }); toast('Added to the do-not-contact list', 'pos'); setOpen(false); onDone() }
    catch (e) { toast((e as Error).message, 'neg') }
  }
  return (
    <>
      <Button variant="ghost" onClick={() => setOpen(true)}>They asked not to be contacted</Button>
      {open && (
        <Modal title="Add to the do-not-contact list" onClose={() => setOpen(false)}
          footer={<><Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={add} disabled={n.replace(/\D/g, '').length < 10}>Add</Button></>}>
          <p style={{ marginTop: 0 }}>The number is taken off this lead and any other not yet sent, and no business is given it again.</p>
          <FormRow label="Their phone number" htmlFor="sc-n"><input id="sc-n" className="field" type="tel" value={n} onChange={(e) => setN(e.target.value)} /></FormRow>
          <FormRow label="Why" htmlFor="sc-why"><input id="sc-why" className="field" value={why} onChange={(e) => setWhy(e.target.value)} /></FormRow>
        </Modal>
      )}
    </>
  )
}

/** Their answer to "What part of town are you in?": the clue that usually settles where they are. */
function LocationAnswer({ id, onDone }: { id: string; onDone: () => void }) {
  const toast = useToast()
  const [v, setV] = useState('')
  const [busy, setBusy] = useState(false)
  const save = async () => {
    setBusy(true)
    try {
      const r = await post<{ zone: { status: string; zone?: string; business?: string | null } | null }>(`/api/my/leads/${id}/location-answer`, { answer: v })
      toast(r.zone?.status === 'resolved' ? `${r.zone.zone}${r.zone.business ? `: recommend ${r.zone.business}` : ''}` : 'Saved. Still not sure where they are: an admin will settle it.', 'pos')
      setV(''); onDone()
    } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }
  return (
    <div className="row-tight" style={{ marginTop: 'var(--s-4)', flexWrap: 'wrap', alignItems: 'center' }}>
      <input className="field" style={{ flex: '1 1 220px' }} aria-label="Where they said they are" placeholder='What they said when asked "What part of town are you in?"' value={v} onChange={(e) => setV(e.target.value)} />
      <Button variant="secondary" onClick={save} disabled={busy || v.trim().length < 2}>Save</Button>
    </div>
  )
}
